import { action, observable } from 'mobx';
import stores from '../../../models/Stores';
import { GraphicsLayer, MapView, SketchViewModel } from 'cd-eft/utils/esriTypes';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { removeLayers, isPointInsidePolygon, getBoundaryPolygon } from 'cd-eft/utils/gisUtils';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { createSaveThumbnail } from 'cd-eft/utils/structureLayerUtils';

const libraryStore = stores.AppStore.projectsLibraryStore;
const { padDesignStore, designStore } = stores.AppStore;

const designController = SETAPI.DesignElementController;
const structureController = SETAPI.StructureController;

export class NewPadVM {
  private mapView: MapView;
  private baseGraphicSketch: SketchViewModel;
  private baseGraphicLayer: GraphicsLayer;
  private mapBlurHandler: IHandle;
  private surfaceModified: boolean = false;
  props;
  constructor(props: NewPadProps) {
    this.props = props;
    this.mapView = props.mapView as MapView;
    if (this.mapView.map.findLayerById('baseStructureGraphics'))
      this.mapView.map.remove(this.mapView.map.findLayerById('baseStructureGraphics'));
    this.baseGraphicLayer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'Base Structure Graphic' });
    this.mapView.map.add(this.baseGraphicLayer);
    this.baseGraphicSketch = new SketchViewModel({
      view: this.mapView,
      layer: this.baseGraphicLayer,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: true,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
        tool: 'transform',
      },
    });
    this.setSketchUpdateEvent();
    this.mapBlurHandler = this.setMapBlurEvent();
  }

  @observable sketchCompleteOrder: 'none' | 'cancel' = 'none';
  @observable error: boolean = false;
  @observable errorMsg: string = '';
  @observable slopeErrorMsg: string = '';
  @observable isLoading: boolean = false;
  @observable newPadForm = padDesignStore.newDesignForm;
  @observable sketchActive: boolean = false;
  private readonly drawInputs: string[] = ['width', 'length', 'orienAzimuth'];

  // Form Validation model
  @observable formValidation = {
    formErrors: {
      name: '',
      x: '',
      y: '',
      z: '',
      width: '',
      length: '',
      orienAzimuth: '',
      'cut-slope-1': '',
      'cut-slope-2': '',
      'cut-slope-3': '',
      'cut-slope-4': '',
      'fill-slope-1': '',
      'fill-slope-2': '',
      'fill-slope-3': '',
      'fill-slope-4': '',
    },
    xValid: false,
    yValid: false,
    zValid: false,
    nameValid: false,
    widthValid: false,
    lengthValid: false,
    orienAzimuthValid: false,
    'cut-slope-1Valid': false,
    'cut-slope-2Valid': false,
    'cut-slope-3Valid': false,
    'cut-slope-4Valid': false,
    'fill-slope-1Valid': false,
    'fill-slope-2Valid': false,
    'fill-slope-3Valid': false,
    'fill-slope-4Valid': false,
    formValid: false,
  };

  @action.bound onMount(history) {
    padDesignStore.showLoader = false;
    if (!designStore.hasProject()) {
      // padDesignStore.showNotification(designStore.noProjectMessage, 'Uh oh!', 'warn');
      MessageCenter.addWarning(designStore.noProjectMessage, [
        {
          callback: () => {
            history?.push('/projects/new-project');
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Project',
        },
      ]);
    } else if (!designStore.hasSurfaceTest()) {
      // padDesignStore.showNotification(designStore.noSurfaceMessage, 'Uh oh!', 'warn');
// designStore.projectsStore.currentSessionId
      MessageCenter.addWarning(designStore.noSurfaceMessage, [ 
        {
          callback: () => {
            history?.push(`/design/${designStore.projectsStore.currentProject.projectUuid}/surface`);
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Surface',
        },
      ]);
    } else {
      // padDesignStore.clearNotification();
      MessageCenter.clearMessages();
      MessageCenter.fillMessageCacheByOriginalCodes([
        'Pad_13', 'Pad_16', 'Pad_17', 'Common_08', 'Common_15',
      ]);
    }
    if (padDesignStore.editDesign) {
      this.getLatestFormValidateStatus();
    }
  }
  @action.bound onUnmount() {
    // mapView Potentially undefined before this unmount
    if (this.mapView && this.mapView.map) {
      this.mapView.map.remove(this.baseGraphicLayer);
      this.props.removeCenterMarker();
    }
    this.mapBlurHandler.remove();
    this.baseGraphicSketch.destroy();
    if (padDesignStore.chooseCenter === true) {
      padDesignStore.chooseCenter = false;
      document.body.style.cursor = 'default';
    }
  }

  @action.bound async getLatestFormValidateStatus() {
    if (!this.formValidation.formValid) {
      // Loop validates field if good in place of user interaction\
      //fields: slope 1-4, width, length, center xyz, and rotation
      for (const field in this.formValidation.formErrors) {
        //the sync of cut slope and fill slope values are handled in handleInputChange
        if (field.includes('cut-slope')) {
          const index = field.replace('cut-slope-', '');
          const value = this.newPadForm.cutSlopes[+index - 1];
          this.validateField(field, value);
        }
        else if (field.includes('fill-slope-')) {
          const index = field.replace('fill-slope-', '');
          const value = this.newPadForm.fillSlopes[+index - 1];
          this.validateField(field, value);
        }
        else {
          const value = this.newPadForm[field];
          this.validateField(field, value);
        }
      }
    }
  }
  @action.bound async checkStructurePendingEdits(inputId) {
    padDesignStore.pendingEdits.unsavedEdits = true;
    if (padDesignStore.newDesign || padDesignStore.editDesign) {
      if (inputId === 'name') {
        padDesignStore.pendingEdits.name = true;
      } else {
        padDesignStore.pendingEdits.design = true;
      }
    }
  }

  @action.bound async handleInputChange(event) {
    let drawMoveRotateBase: boolean = false;
    const { target } = event;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;

    // if value is number keep it a number
    if (!isNaN(parseFloat(value)) && isFinite(value) && !target.id.includes('name')) {
      value = +value;
    }

    this.checkStructurePendingEdits(target.id);

    if (target.id.includes('center')) {
      this.newPadForm.center[name] = value;
      drawMoveRotateBase = true;
    } else if (target.id.includes('cut-slope')) {
      if (this.newPadForm.equalCutSlopes) {
        //sync cut slope, ensure that all the slopes have same value
        this.newPadForm.cutSlopes.forEach((v, i) => {
          this.newPadForm.cutSlopes[i] = value;
        });
      } else {
        const index = target.id.replace('cut-slope-', '');
        this.newPadForm.cutSlopes[+index - 1] = value;
      }
    } else if (target.id.includes('fill-slope')) {
      if (this.newPadForm.equalFillSlopes) {
        //sync fill slope, ensure that all the slopes have same value
        this.newPadForm.fillSlopes.forEach((v, i) => {
          this.newPadForm.fillSlopes[i] = value;
        });
      } else {
        const index = target.id.replace('fill-slope-', '');
        this.newPadForm.fillSlopes[+index - 1] = value;
      }
    } else {
      this.newPadForm[name] = value;
      if (
        this.drawInputs.find((input) => {
          return input === name;
        })
      ) {
        drawMoveRotateBase = true;
      }
    }
    if (target.id.includes('cut-slope') && this.newPadForm.equalCutSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'cut-slope');
    } else if (target.id.includes('fill-slope') && this.newPadForm.equalFillSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'fill-slope');
    } else {
      await this.validateField(name, value);
    }
    if (drawMoveRotateBase && (target.id.includes('center') || this.formValidation[name + 'Valid'])) {
      this.props.drawRectangularBase(padDesignStore.newDesignForm);
    }
  }

  @action.bound shareSlopeValidation(originSlope: string, slopeGroup: string) {
    for (let i = 1; i < 5; i++) {
      const name = slopeGroup + `-${i}`;
      this.formValidation.formErrors[name] = this.formValidation.formErrors[originSlope];
      this.formValidation[name + 'Valid'] = this.formValidation[originSlope + 'Valid'];
    }
    this.slopeErrorMsg = this.getSlopeErrorMessage();
    this.validateForm();
  }

  @action.bound toggleEqualFillSlopes() {
    this.newPadForm.equalFillSlopes = !this.newPadForm.equalFillSlopes;
    // make fill slopes equal on form
    if (this.newPadForm.equalFillSlopes) {
      this.newPadForm.fillSlopes.forEach((value, i) => {
        this.newPadForm.fillSlopes[i] = this.newPadForm.fillSlopes[0];
        this.validateField(`fill-slope-${i + 1}`, this.newPadForm.fillSlopes[i]);
      });
    }
  }

  @action.bound toggleEqualCutSlopes() {
    this.newPadForm.equalCutSlopes = !this.newPadForm.equalCutSlopes;
    // make cut slopes equal on form
    if (this.newPadForm.equalCutSlopes) {
      this.newPadForm.cutSlopes.forEach((value, i) => {
        this.newPadForm.cutSlopes[i] = this.newPadForm.cutSlopes[0];
        this.validateField(`cut-slope-${i + 1}`, this.newPadForm.cutSlopes[i]);
      });
    }
  }

  @action.bound async checkNameDuplicate() {
    const nameDuplicate = padDesignStore.padSummaryTable.some((summary) => {
      return summary.designElementName.toLowerCase() === padDesignStore.newDesignForm.name.toLowerCase() &&
        padDesignStore.newDesignForm.designElementId !== summary.designElementId;
    });
    if (nameDuplicate) {
      /*       const errorCode = 'Common_07';
            const fieldName = 'name'
            await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName); */
      await MessageCenter.addErrorFromCode('Common_07');
      return true;
    }
    return false;
  }

  @action.bound clearCenterErrorMessages() {
    for (const fieldName of ['x', 'y', 'z']) {
      this.formValidation.formErrors[fieldName] = '';
      this.formValidation[`${fieldName}Valid`] = true;
    }
  }

  @action.bound async generateStructure() {
    this.sketchCompleteOrder = 'cancel';
    // check form validation - defaults to false until interacted with
    //if current form is invalid, validate all the fields to get the latest condition.
    this.getLatestFormValidateStatus();
    // If the current form with the latest changes is invalid - return and halt execution
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (!this.formValidation.formValid || checkNameDuplicate) {
      return;
    }
    //form is valid, post data to backend using API
    const postPadJSON: IgeneratePadPostData = {
      name: this.newPadForm.name,
      type: this.newPadForm.type,
      equalCutSlopes: this.newPadForm.equalCutSlopes,
      equalFillSlopes: this.newPadForm.equalFillSlopes,
      center: this.newPadForm.center,
      width: this.newPadForm.width,
      length: this.newPadForm.length,
      orienAzimuth: this.newPadForm.orienAzimuth,
      designElementId: designStore.currentSurface.designElementId,
      cutSlopes: this.newPadForm.cutSlopes,
      fillSlopes: this.newPadForm.fillSlopes,
    };
    padDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';

    const bPolygon = getBoundaryPolygon(libraryStore.projectSpatialReference);
    const x = this.newPadForm.center.x;
    const y = this.newPadForm.center.y;

    const pointInsidePolygon = await isPointInsidePolygon(x, y, bPolygon, libraryStore.projectSpatialReference!);

    if (!pointInsidePolygon) {
      MessageCenter.addErrorFromCode('Elev_01');
      padDesignStore.showLoader = false;
      this.isLoading = false;
      return;
    }

    try {
      const response = (await structureController.generatePadUsingPOST(postPadJSON)).data;
      if (response.designElementId && this.mapView) {
        padDesignStore.newPadResults = response as any;
        designStore.currentDesignElementJson = response as any;
        designStore.currentDesignElementJson.designedContours = null;
        designStore.currentDesignElementJson.designedTinTriangles = null;
        
        removeLayers([padDesignStore.currentDesignLayer], this.mapView);
        const surfaceModified = await DesignContainerVM.loadSurfaceFromData(
          response.designedTinTriangles!,
          undefined,
          response.designedContours,
          response.wkid ? parseInt(response.wkid) : undefined
        );
        this.surfaceModified = this.surfaceModified || surfaceModified;
        padDesignStore.addPadToMap(
          response,
          this.mapView,
          true,
          libraryStore.projectSpatialReference!,
          padDesignStore.originalIdString ? padDesignStore.originalIdString : undefined
        );

        padDesignStore.newDesign = true;
        this.props.removeCenterMarker();
      }
      if (response.warningMsg) {
        // padDesignStore.showNotification(response.warningMsg, 'Warning', 'warn');
        MessageCenter.addWarning(response.warningMsg);
      } else {
        // padDesignStore.clearNotification();
        MessageCenter.clearMessages();
        //new design is generated successfully based on pending edits
        padDesignStore.pendingEdits.name = false;
        padDesignStore.pendingEdits.design = false;
        padDesignStore.pendingEdits.unsavedEdits = true;
      }
      padDesignStore.showLoader = false;
      this.isLoading = false;
      this.error = false;
      designStore.structureLayerExists = true;
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        // padDesignStore.showNotification((error as any).response.data.errorMessage, 'Error');
        MessageCenter.addError((error as any).response.data.errorMessage);
      } else {
        // padDesignStore.showNotification((error as any).message, 'Error');
        MessageCenter.addError((error as any).message);
      }
      //post data has error, reset pending edits
      padDesignStore.pendingEdits.name = true;
      padDesignStore.pendingEdits.design = true;
      padDesignStore.showLoader = false;
      this.isLoading = false;
      this.error = true;
    }
  }

  // @action.bound pickUpCenter() {
  //   // Want this to create map event to allow user to click on map and get cords
  //   if (padDesignStore.chooseCenter === true) {
  //     padDesignStore.chooseCenter = false;
  //     document.body.style.cursor = 'default';
  //   } else {
  //     padDesignStore.chooseCenter = true;
  //     document.body.style.cursor = 'crosshair';
  //   }
  // }

  @action.bound resetOriginalPadDesignLayer() {
    if (
      padDesignStore.originalDesignLayer &&
      padDesignStore.editDesign &&
      padDesignStore.currentDesignLayer !== padDesignStore.originalDesignLayer
    ) {
      const index = padDesignStore.padLayers.findIndex((layer) => layer.id === padDesignStore.currentDesignLayer.id);
      // pitDesignStore.pitLayers[index] = pitDesignStore.originalDesignLayer;
      padDesignStore.padLayers.splice(index, 1);
      removeLayers([padDesignStore.currentDesignLayer], this.mapView);
      // pitDesignStore.currentDesignLayer = pitDesignStore.originalDesignLayer;
      padDesignStore.newPadResults = padDesignStore.originalPadResults;
      padDesignStore.addPadToMap(padDesignStore.newPadResults, this.mapView, true, libraryStore.projectSpatialReference!, padDesignStore.originalIdString);
    }
  }

  @action.bound async cancelAddDesignElement(resetMostRecent: boolean = false, cancelEdits: boolean = true) {
    designStore.onNewStructureTab = false;
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    this.sketchCompleteOrder = 'cancel';
    if (cancelEdits) this.resetOriginalPadDesignLayer();
    const summaryEntry = padDesignStore.padSummaryTable.find(
      (entry) => entry.designElementId.toString() === padDesignStore.currentDesignLayer.id
    );
    if (this.mapView) {
      if (!summaryEntry || !summaryEntry.checked) removeLayers([padDesignStore.currentDesignLayer], this.mapView);
      this.props.removeCenterMarker();
      this.props.removeBaseStructureGraphics();
      if (resetMostRecent && this.surfaceModified) {
        this.surfaceModified = !(await DesignContainerVM.displayOriginalSurface());
      }
      if (!designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.mapView);
    }
    designStore.currentDesignElementJson = null;
    padDesignStore.cancelDesignElement();
  }

  @action.bound async updateDesignElement() {
    designStore.currentDesignElementJson = null;
    this.sketchCompleteOrder = 'cancel';
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    if (padDesignStore.pendingEdits.name) {
      padDesignStore.newPadResults!.name = padDesignStore.newDesignForm.name;
    }
    const newPadResultsCopy = padDesignStore.newPadResults as any;
    newPadResultsCopy.designedContours = null;
    newPadResultsCopy.designedTinTriangles = null;
    const updateDesignData = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      designElementId: padDesignStore.newDesignForm.designElementId,
      designElementName: padDesignStore.newDesignForm.name,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementTypeId: 14, // Pad
      json: JSON.stringify(newPadResultsCopy),
    };
    this.error = false;
    try {
      const response = (await designController.updateDesignElementUsingPOST(updateDesignData)).data;
      if (response.designElementId && this.mapView) {
        removeLayers([padDesignStore.currentDesignLayer], this.mapView);
        const label = (await structureController.generateConstructionReportJsonByDesignElementJsonIdAndDesignElementTypeIdUsingPOST(
          response.designElementId,
          updateDesignData.designElementTypeId,
        )).data;
        padDesignStore.addPadToMap(
          padDesignStore.newPadResults,
          this.mapView,
          false,
          libraryStore.projectSpatialReference!,
          response.designElementId.toString(),
          label
        );
        padDesignStore.latestElementId = response.designElementId;
        this.cancelAddDesignElement(false, false);
        padDesignStore.pendingEdits.unsavedEdits = false;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId);
        });

      }
    } catch (error) {
      this.error = true;
      console.error('Error:', error);
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage);
      } else {
        MessageCenter.addError((error as any).message);
      }
    }
  }

  @action.bound async postAddDesignElement() {
    designStore.onNewStructureTab = false;
    designStore.currentDesignElementJson = null;
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    this.sketchCompleteOrder = 'cancel';
    const newPadResultsCopy = padDesignStore.newPadResults as any;
    newPadResultsCopy.designedContours = null;
    newPadResultsCopy.designedTinTriangles = null;
    const addDesignData = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementName: padDesignStore.newDesignForm.name,
      designElementTypeId: 14, // Pad
      json: JSON.stringify(newPadResultsCopy),
    };

    const postJSON = addDesignData;
    postJSON.json = JSON.stringify(padDesignStore.newPadResults);
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';

    try {
      const response = (await designController.addDesignElementUsingPOST(postJSON)).data;
      if (response.designElementId && this.mapView) {
        removeLayers([padDesignStore.currentDesignLayer], this.mapView);
        const label = (await structureController.generateConstructionReportJsonByDesignElementJsonIdAndDesignElementTypeIdUsingPOST(
          response.designElementId,
          addDesignData.designElementTypeId,
        )).data;
        const responseParsed = JSON.parse(response.json!);
        padDesignStore.addPadToMap(
          responseParsed,
          this.mapView,
          false,
          libraryStore.projectSpatialReference!,
          response.designElementId.toString(),
          label
        );
        padDesignStore.latestElementId = response.designElementId;
        padDesignStore.cancelDesignElement();
        designStore.structureLayerExists = true;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId);
        });

        padDesignStore.displayedPadsIds = [];
        padDesignStore.displayedPadsIds.push(response.designElementId);
      }

      this.isLoading = false;
      this.error = false;
      this.errorMsg = '';
      padDesignStore.pendingEdits.unsavedEdits = false;
    } catch (error) {
      this.error = true;
      console.error('Error:', error);
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage);
      } else {
        MessageCenter.addError((error as any).message);
      }
      this.isLoading = false;
    }
  }

  // form validation by field
  @action.bound async validateField(fieldName, value) {
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    let skipCheck = false;
    let errorCode: string = '';
    switch (fieldName) {
      case 'cut-slope-1':
      case 'cut-slope-2':
      case 'cut-slope-3':
      case 'cut-slope-4':
      case 'fill-slope-1':
      case 'fill-slope-2':
      case 'fill-slope-3':
      case 'fill-slope-4':
        fieldValid = value > 0;
        fieldValidationErrors[fieldName] = '  '; // No known error message yet, but needs to be non empty for equal slope validation above.
        errorCode = fieldValid ? '' : 'Pad_13';
        break;
      case 'width':
        fieldValid = value > 0 && value <= 1000;
        errorCode = fieldValid ? '' : 'Pad_14';
        break;
      case 'length':
        fieldValid = value > 0 && value <= 1000;
        errorCode = fieldValid ? '' : 'Pad_15';
        break;
      case 'orienAzimuth':
        fieldValid = value >= 0 && value <= 360;
        errorCode = fieldValid ? '' : 'Pad_16';
        break;
      case 'name':
        fieldValid = value !== '';
        //const checkNameDuplicate = await this.checkNameDuplicate();
        // if (checkNameDuplicate) errorCode = 'Common_07';
        // else errorCode = fieldValid ? '' : 'Pad_17';
        await this.checkNameDuplicate();
        errorCode = fieldValid ? '' : 'Pad_17';
        break;
      case 'x':
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08'; // value is required.
        break;
      case 'y':
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08'; // value is required.
        break;
      case 'z':
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08'; // value is required.
        break;
      default:
        skipCheck = true;
        break;
    }
    this.formValidation.formErrors = fieldValidationErrors;
    if (!skipCheck) await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
    } else {
      const message = await MessageCenter.addErrorLabelFromCode(code);
      container[fieldName] = message;
    }
    if (fieldName.includes('slope')) {
      this.slopeErrorMsg = this.getSlopeErrorMessage();
    }
    return;
  }

  // checks if there are any error msgs over entire form
  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound getSlopeErrorMessage() {
    //if error message is not empty return
    let slopeErrorMessage = '';
    for (let i = 1; i < 5; i++) {
      if (this.formValidation.formErrors['cut-slope-' + i] !== '') {
        slopeErrorMessage = this.formValidation.formErrors['cut-slope-' + i];
        break;
      } else if (this.formValidation.formErrors['fill-slope-' + i] !== '') {
        slopeErrorMessage = this.formValidation.formErrors['fill-slope-' + i];
        break;
      }
    }
    return slopeErrorMessage;
  }
  // END Form Validation model

  @action.bound handleOnBlurRounding(event) {
    const target = event.target;
    const oldValue = +target.value;
    const { name, id } = target;
    const sharedAdjustedValue = toDecimalPrecision(oldValue, 3);
    if (id.includes('center')) {
      this.newPadForm.center[name] = toDecimalPrecision(sharedAdjustedValue, 3);
    } else if (id.includes('fill-slope')) {
      if (this.newPadForm.equalFillSlopes) {
        this.newPadForm.fillSlopes.forEach((v, i) => {
          this.newPadForm.fillSlopes[i] = toDecimalPrecision(sharedAdjustedValue, 3);
        });
      } else {
        const index = target.id.replace('fill-slope-', '');
        this.newPadForm.fillSlopes[+index - 1] = toDecimalPrecision(sharedAdjustedValue, 3);
      }
    } else if (id.includes('cut-slope')) {
      if (this.newPadForm.equalCutSlopes) {
        this.newPadForm.cutSlopes.forEach((v, i) => {
          this.newPadForm.cutSlopes[i] = toDecimalPrecision(sharedAdjustedValue, 3);
        });
      } else {
        const index = target.id.replace('cut-slope-', '');
        this.newPadForm.cutSlopes[+index - 1] = toDecimalPrecision(sharedAdjustedValue, 3);
      }
    } else {
      this.newPadForm[name] = toDecimalPrecision(sharedAdjustedValue, 3);
    }
    this.validateField(name, sharedAdjustedValue);
  }

  //user leaving notification section
  @observable notification: any = {
    openPad: '',
  };

  @action.bound async getErrors() {
    let message = await MessageCenter.addErrorLabelFromCode('Survey_21'); // 'Please Save or Cancel Pending Edits before Leaving Surface Design.';
    message = message.replace('Surface', 'Pad');
    this.notification.openPad = message;
  }

  @action.bound userLeavingWarningOnOk() {
    return false;
  }
  //end of user leaving notification section

  @action.bound activateMoveRotateSketch() {
    if (this.baseGraphicLayer.graphics.length === 1 && !this.sketchActive) {
      this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
      this.sketchActive = true;
    } else {
      if (this.baseGraphicSketch === undefined) {
        this.initializeBaseSketch();
      }
      this.drawBase();
    }
  }

  private drawBase() {
    const form = padDesignStore.newDesignForm;
    if (form.center.x > 0 && form.center.y > 0) {
      if (form.width > 0 && form.length > 0) {
        this.props.drawRectangularBase(padDesignStore.newDesignForm, false);
        this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
        this.sketchActive = true;
      } else {
        this.sketchActive = false;
      }
    } else {
      this.sketchActive = false;
    }
  }

  private initializeBaseSketch() {
    if (this.mapView && this.mapView.map.findLayerById('baseStructureGraphics'))
      this.mapView.map.remove(this.mapView.map.findLayerById('baseStructureGraphics'));
    this.baseGraphicLayer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'Base Structure Graphic' });
    this.mapView.map.add(this.baseGraphicLayer);
    this.baseGraphicSketch = new SketchViewModel({
      view: this.mapView,
      layer: this.baseGraphicLayer,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: true,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
        tool: 'transform',
      },
    });
    this.setSketchUpdateEvent();
    this.mapBlurHandler = this.setMapBlurEvent();
  }

  private setSketchUpdateEvent() {
    this.baseGraphicSketch.on('update', async (event) => {
      const eventInfo = event.toolEventInfo;
      if (eventInfo && eventInfo.type === 'scale-start') {
        this.baseGraphicSketch.complete();
        return;
      }
      if (
        event.state !== 'complete' &&
        eventInfo &&
        (eventInfo.type === 'move-stop' || eventInfo.type === 'rotate-stop')
      ) {
        this.props.updateBaseGraphic(padDesignStore.newDesignForm);
      }
      if (event.state === 'complete') {
        if (padDesignStore.currentTab === 'New Pad') {
          // console.log('generate structure call');
          switch (this.sketchCompleteOrder) {
            case 'none':
              // console.log('no special case');
              this.generateStructure();
              this.sketchActive = false;
              break;
            case 'cancel':
              // console.log('cancel case');
              this.sketchActive = false;
              break;
          }
          this.sketchCompleteOrder = 'none';
        } else {
          this.sketchActive = false;
        }

        // this.generateStructure();
        // this.sketchActive = false;
      }
    });
  }

  private setMapBlurEvent(): IHandle {
    return this.mapView.on('blur', async () => {
      if (this.baseGraphicSketch.state === 'active') {
        // console.log('map blur with avtive sketch');
        await setTimeout(() => {
          // console.log('wait to fire complete event');
          this.baseGraphicSketch.complete();
        }, 300);
        // this.baseGraphicSketch.set('state', 'ready');
      }
    });
  }

  async saveThumbnail(snapShot: any, designElementId: number | undefined, designElementTypeId: number | undefined) {
    if (snapShot && designElementTypeId !== undefined && designElementId !== undefined) {
      const payloadForMap = {
        designElementId: designElementId,
        designElementTypeId: designElementTypeId,
        profileImageBase64String: undefined,
        profileOrCrossSection: undefined,
        stationId: undefined,
        structureMapImageBase64String: snapShot.dataUrl,
        structureProfilesId: 0,
        twoDimensionalGeometryJson: undefined
      };

      try {
        await SETAPI.StructureProfileController.generateUpdateStructureMapImageUsingPOST(payloadForMap);
      } catch (error) {
        throw error;
      }
    }
  }
}


// NewPadVM Interface
interface IgeneratePadPostData {
  name: string;
  type: string;
  equalCutSlopes: boolean;
  equalFillSlopes: boolean;
  center: {
    pointName: string;
    x: number;
    y: number;
    z: number;
  };
  width: number;
  length: number;
  orienAzimuth: number;
  designElementId: number;
  cutSlopes: number[];
  fillSlopes: number[];
}

interface NewPadProps {
  mapView: MapView | undefined;
  drawRectangularBase: (designForm: any) => void;
  updateBaseGraphic: (designForm: any) => void;
  removeBaseStructureGraphics: () => void;
  removeCenterMarker: () => void;
}
